export type ScienceMasteryFocus =
  | 'earth-change'
  | 'relationships'
  | 'materials'
  | 'weather'
  | 'living-things'
  | 'sound';

export interface ScienceMasteryChoice {
  readonly id: string;
  readonly text: string;
}

export interface ScienceMasteryTrial {
  readonly id: string;
  readonly skillId: string;
  readonly focus: ScienceMasteryFocus;
  readonly evidenceRecord: string;
  readonly prompt: string;
  readonly choices: readonly ScienceMasteryChoice[];
  readonly correctChoiceId: string;
  readonly explanation: string;
  readonly hints: readonly string[];
}

export const DISCOVERY_OBSERVATORY_RESTORATION_EVENT =
  'discovery-observatory-fully-restored';

export const DISCOVERY_OBSERVATORY_TRIALS: readonly ScienceMasteryTrial[] = [
  {
    id: 'lens-earth-change',
    skillId: 'science-mastery-earth-change',
    focus: 'earth-change',
    evidenceRecord:
      'Gentle-flow tray: a little soil moved. Strong-flow tray: much more soil moved and a deeper channel formed.',
    prompt: 'Which claim does the model support?',
    choices: [
      { id: 'strong-more', text: 'Stronger moving water can carry more soil.' },
      { id: 'gentle-more', text: 'Gentle water always carries more soil.' },
      { id: 'same-change', text: 'Both flows always reshape land exactly the same way.' },
    ],
    correctChoiceId: 'strong-more',
    explanation: 'The strong-flow tray lost more soil and formed the deeper channel.',
    hints: [
      'Compare how much soil moved in both trays.',
      'Look for the flow that formed the deeper channel.',
      'The stronger flow caused the larger modeled change.',
    ],
  },
  {
    id: 'lens-relationships',
    skillId: 'science-mastery-relationships',
    focus: 'relationships',
    evidenceRecord:
      'Visited flowers received pollen and later formed fruit. Covered flowers received no visits and formed no fruit in the model.',
    prompt: 'Which explanation uses both observations?',
    choices: [
      { id: 'visits-help', text: 'Pollinator visits can help flowering plants make fruit.' },
      { id: 'covers-help', text: 'Covering every flower helps it make more fruit.' },
      { id: 'unrelated', text: 'Flower visits and fruit formation are never related.' },
    ],
    correctChoiceId: 'visits-help',
    explanation: 'Only the visited flowers received pollen and formed fruit in the model.',
    hints: [
      'Notice which flowers received pollen.',
      'Compare the later fruit at visited and covered flowers.',
      'The model connects pollinator visits with fruit formation.',
    ],
  },
  {
    id: 'lens-materials',
    skillId: 'science-mastery-materials',
    focus: 'materials',
    evidenceRecord:
      'Paper absorbed water and sagged. Cloth let drops through. The waterproof sheet stayed firm and shed the drops.',
    prompt: 'Which material best fits a model shelter roof?',
    choices: [
      { id: 'paper', text: 'Paper, because it absorbed the water.' },
      { id: 'cloth', text: 'Cloth, because drops passed through it.' },
      { id: 'sheet', text: 'The waterproof sheet, because it stayed firm and shed water.' },
    ],
    correctChoiceId: 'sheet',
    explanation: 'A shelter roof must block water, and only the waterproof sheet did that.',
    hints: [
      'Think about the purpose of a roof.',
      'Find the material that kept water from passing through.',
      'The waterproof sheet stayed firm and shed the drops.',
    ],
  },
  {
    id: 'lens-weather',
    skillId: 'science-mastery-weather',
    focus: 'weather',
    evidenceRecord:
      'Morning 1 was sunny and dry. Morning 2 had more clouds and was cooler. Morning 3 was overcast, coolest, and rainy.',
    prompt: 'Which pattern is supported by the three records?',
    choices: [
      { id: 'clouds-cool-rain', text: 'More cloud cover came with cooler readings, then rain.' },
      { id: 'sun-rain', text: 'The sunniest morning had the most rain.' },
      { id: 'warmer-cloudy', text: 'Each cloudier morning was warmer than the last.' },
    ],
    correctChoiceId: 'clouds-cool-rain',
    explanation: 'The records show increasing clouds, decreasing temperature, and rain on the overcast morning.',
    hints: [
      'Compare clouds, temperature, and precipitation together.',
      'Follow how the readings changed from Morning 1 to Morning 3.',
      'Clouds increased while temperatures decreased; rain came last.',
    ],
  },
  {
    id: 'lens-living-things',
    skillId: 'science-mastery-living-things',
    focus: 'living-things',
    evidenceRecord:
      'The light-and-water plant grew tallest. The pond site supplied a frog with water, food, and shelter. Frog eggs changed into tadpoles, froglets, and adults.',
    prompt: 'Which statement is supported by the living-things records?',
    choices: [
      { id: 'needs-growth-change', text: 'Living things need suitable conditions, grow, and change during life cycles.' },
      { id: 'no-needs', text: 'Living things grow the same without water, food, or shelter.' },
      { id: 'no-change', text: 'Young animals always look exactly like adults.' },
    ],
    correctChoiceId: 'needs-growth-change',
    explanation: 'The plant and habitat records show needs, and the frog record shows growth and change.',
    hints: [
      'Use evidence from the plant, habitat, and frog records.',
      'Look for a statement that fits all three records.',
      'Suitable conditions support life, while life cycles show growth and change.',
    ],
  },
  {
    id: 'lens-sound',
    skillId: 'science-mastery-sound',
    focus: 'sound',
    evidenceRecord:
      'The still surface had no sound reading. A small modeled vibration matched a quiet sound. A larger vibration matched a louder sound.',
    prompt: 'Which conclusion is supported?',
    choices: [
      { id: 'vibration-sound', text: 'Sound comes from vibration, and larger modeled vibrations can match louder sounds.' },
      { id: 'still-loud', text: 'A still object always makes the loudest sound.' },
      { id: 'no-relationship', text: 'Vibration and sound have no relationship.' },
    ],
    correctChoiceId: 'vibration-sound',
    explanation: 'Sound appeared when the model vibrated, and the larger vibration matched the louder reading.',
    hints: [
      'Compare the still, small-motion, and large-motion records.',
      'Notice when the model first showed sound.',
      'Vibration matched sound, and larger motion matched louder sound.',
    ],
  },
];

export function validateDiscoveryObservatoryTrials(
  trials = DISCOVERY_OBSERVATORY_TRIALS,
): true {
  const expected = new Set<ScienceMasteryFocus>([
    'earth-change',
    'relationships',
    'materials',
    'weather',
    'living-things',
    'sound',
  ]);
  const ids = new Set<string>();
  const focuses = new Set<ScienceMasteryFocus>();
  trials.forEach(trial => {
    if (ids.has(trial.id)) throw new RangeError(`Duplicate Discovery Observatory trial: ${trial.id}`);
    ids.add(trial.id);
    focuses.add(trial.focus);
    const choiceIds = trial.choices.map(choice => choice.id);
    if (
      trial.choices.length !== 3
      || new Set(choiceIds).size !== choiceIds.length
      || !choiceIds.includes(trial.correctChoiceId)
    ) throw new RangeError(`Invalid choices for ${trial.id}.`);
    if (
      !trial.evidenceRecord.trim()
      || !trial.prompt.trim()
      || !trial.explanation.trim()
      || trial.hints.length < 3
    ) throw new RangeError(`Incomplete mastery content for ${trial.id}.`);
  });
  if (trials.length !== expected.size || [...expected].some(focus => !focuses.has(focus))) {
    throw new RangeError('Discovery Observatory must cover all six science mastery focuses.');
  }
  return true;
}

validateDiscoveryObservatoryTrials();
